import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { BedDouble, LoaderCircle, Calendar as CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { toLocalYMD as ymd } from "@/lib/dateUtils";

const DAYS_TR = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const MONTHS_TR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

const fmtDay = (s) => {
    try {
        const d = new Date(`${s}T12:00:00`);
        return `${d.getDate()} ${MONTHS_TR[d.getMonth()]}`;
    } catch { return s; }
};
const fmtWeekday = (s) => {
    try {
        const d = new Date(`${s}T12:00:00`);
        return DAYS_TR[d.getDay()];
    } catch { return ""; }
};

const RANGE_OPTIONS = [
    { v: 14, label: "14 gün" },
    { v: 30, label: "30 gün" },
    { v: 60, label: "60 gün" },
    { v: 90, label: "90 gün" },
];

/**
 * Day-by-day stacked occupancy bar chart.
 * yellow = free (boş)  ·  blue = sold (satılan)
 */
export default function OccupancyChart({ hotelId, days: initialDays = 30, title = "Doluluk Grafiği", compact = false }) {
    const [days, setDays] = useState(initialDays);
    // startDate: takvim penceresinin başlangıç günü (varsayılan: bugün)
    const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return ymd(d); });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    const { startStr, endStr } = useMemo(() => {
        const start = new Date(`${startDate}T00:00:00`);
        const end = new Date(start); end.setDate(end.getDate() + days - 1);
        return { startStr: ymd(start), endStr: ymd(end) };
    }, [startDate, days]);

    useEffect(() => {
        if (!hotelId) return;
        setLoading(true);
        setErr(null);
        api.get(`/admin/hotels/${hotelId}/occupancy-chart?start=${startStr}&end=${endStr}`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(e.response?.data?.detail || "Veri alınamadı"))
            .finally(() => setLoading(false));
    }, [hotelId, startStr, endStr]);

    const shiftDays = (delta) => {
        const d = new Date(`${startDate}T00:00:00`);
        d.setDate(d.getDate() + delta);
        setStartDate(ymd(d));
    };
    const goToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); setStartDate(ymd(d)); };
    const goPastPreset = () => {
        // Son N gün: today - N + 1
        const d = new Date();
        d.setDate(d.getDate() - days + 1);
        d.setHours(0, 0, 0, 0);
        setStartDate(ymd(d));
    };

    const items = data?.items || [];
    const totalRooms = data?.total_rooms || 0;

    const summary = useMemo(() => {
        if (!items.length || !totalRooms) return { avgOcc: 0, totalSold: 0, totalCapacity: 0 };
        const totalSold = items.reduce((a, b) => a + (b.sold || 0), 0);
        const totalCapacity = totalRooms * items.length;
        const avgOcc = totalCapacity > 0 ? (totalSold / totalCapacity) * 100 : 0;
        return { avgOcc: Math.round(avgOcc * 10) / 10, totalSold, totalCapacity };
    }, [items, totalRooms]);

    const todayYmd = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return ymd(d); }, []);
    const isToday = startDate === todayYmd;

    return (
        <section data-testid="occupancy-chart" className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                <div className="min-w-0">
                    <h2 className="font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <BedDouble className="h-4 w-4 text-sky-500" /> {title}
                    </h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Günlük toplam oda kapasitesi vs satılan oda · <span className="font-semibold text-sky-600 dark:text-sky-400">mavi=satılan</span> · <span className="font-semibold text-amber-500">sarı=boş</span>
                    </p>
                </div>
                {!compact && (
                    <div className="flex items-center gap-1" data-testid="occ-range-switcher">
                        {RANGE_OPTIONS.map((r) => (
                            <button
                                key={r.v}
                                data-testid={`occ-range-${r.v}d`}
                                onClick={() => setDays(r.v)}
                                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${days === r.v ? "bg-sky-500 text-white border-sky-500" : "bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700"}`}
                            >{r.label}</button>
                        ))}
                    </div>
                )}
            </div>

            {/* Date navigation bar */}
            {!compact && (
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <button data-testid="occ-prev-period" onClick={() => shiftDays(-days)} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-[11px] font-semibold" title={`${days} gün geri`}>
                        <ChevronLeft className="h-3.5 w-3.5" /> Önceki
                    </button>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => e.target.value && setStartDate(e.target.value)}
                        data-testid="occ-date-picker"
                        className="px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] font-mono"
                    />
                    <button data-testid="occ-next-period" onClick={() => shiftDays(days)} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-[11px] font-semibold" title={`${days} gün ileri`}>
                        Sonraki <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                    <div className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
                    <button data-testid="occ-past-preset" onClick={goPastPreset} className="px-2.5 py-1 rounded-lg text-[11px] font-semibold border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700" title={`Geçmiş ${days} gün`}>
                        Geçmiş {days} gün
                    </button>
                    <button data-testid="occ-today-btn" onClick={goToday} className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition-colors ${isToday ? "bg-sky-500 text-white border-sky-500" : "border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"}`}>
                        Bugün
                    </button>
                </div>
            )}

            {/* Summary strip */}
            <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800/60">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Toplam Oda</div>
                    <div className="text-lg font-bold text-slate-900 dark:text-white" data-testid="occ-total-rooms">{totalRooms}</div>
                </div>
                <div className="px-3 py-2 rounded-xl bg-sky-50 dark:bg-sky-950/30">
                    <div className="text-[10px] uppercase tracking-widest text-sky-600 dark:text-sky-400 font-bold">Satılan (Toplam)</div>
                    <div className="text-lg font-bold text-sky-700 dark:text-sky-300" data-testid="occ-total-sold">{summary.totalSold}</div>
                </div>
                <div className="px-3 py-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/30">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-600 dark:text-emerald-400 font-bold">Ort. Doluluk</div>
                    <div className="text-lg font-bold text-emerald-700 dark:text-emerald-300" data-testid="occ-avg-pct">%{summary.avgOcc}</div>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-16"><LoaderCircle className="h-6 w-6 animate-spin text-sky-500" /></div>
            ) : err ? (
                <div className="p-8 text-center text-sm text-rose-600" data-testid="occ-error">{err}</div>
            ) : !totalRooms ? (
                <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2" data-testid="occ-empty">
                    <CalendarIcon className="h-4 w-4" /> Bu otelde henüz oda tanımlı değil.
                </div>
            ) : (
                <>
                    {/* Stacked bars */}
                    <div className="relative" data-testid="occ-bars-wrap">
                        <div className="flex items-end gap-[2px] h-44 px-0.5">
                            {items.map((d) => {
                                const soldPct = totalRooms > 0 ? (d.sold / totalRooms) * 100 : 0;
                                const freePct = 100 - soldPct;
                                return (
                                    <div
                                        key={d.date}
                                        data-testid={`occ-bar-${d.date}`}
                                        className="flex-1 group relative flex flex-col-reverse h-full min-w-[6px]"
                                        title={`${d.date} — Satılan: ${d.sold} / ${d.total} (%${d.occupancy_pct}) · Boş: ${d.free}`}
                                    >
                                        {/* sold (blue, bottom) */}
                                        <div
                                            className="w-full bg-gradient-to-t from-sky-600 to-sky-400 group-hover:from-sky-700 group-hover:to-sky-500 transition-colors"
                                            style={{ height: `${soldPct}%` }}
                                        />
                                        {/* free (yellow, top) */}
                                        <div
                                            className="w-full bg-gradient-to-t from-amber-400 to-amber-300 group-hover:from-amber-500 group-hover:to-amber-400 transition-colors"
                                            style={{ height: `${freePct}%` }}
                                        />
                                        {/* Tooltip */}
                                        <div className="pointer-events-none absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-10 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="text-[10px] bg-slate-900 text-white px-2 py-1 rounded-lg shadow-lg whitespace-nowrap">
                                                <div className="font-bold">{fmtDay(d.date)} · {fmtWeekday(d.date)}</div>
                                                <div className="text-sky-300">Satılan: {d.sold} (%{d.occupancy_pct})</div>
                                                <div className="text-amber-200">Boş: {d.free}</div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* X-axis labels (sparse: every Nth day) */}
                        <div className="flex gap-[2px] mt-1.5 px-0.5">
                            {items.map((d, i) => {
                                const step = items.length > 30 ? 7 : items.length > 14 ? 3 : 2;
                                const show = i === 0 || i === items.length - 1 || i % step === 0;
                                return (
                                    <div key={d.date} className="flex-1 min-w-[6px] text-center">
                                        {show && (
                                            <div className="text-[9px] text-slate-500 dark:text-slate-400 font-medium leading-tight">
                                                <div>{fmtDay(d.date).split(" ")[0]}</div>
                                                <div className="opacity-70">{fmtWeekday(d.date)}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Legend */}
                    <div className="flex items-center justify-center gap-5 mt-3 text-[11px]">
                        <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-sm bg-gradient-to-t from-sky-600 to-sky-400 inline-block" /> Satılan
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-sm bg-gradient-to-t from-amber-400 to-amber-300 inline-block" /> Boş
                        </div>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500">{startStr} → {endStr}</span>
                    </div>
                </>
            )}
        </section>
    );
}
